// Shared vocabulary for the RADIUS policy screens.
//
// A RADIUS login is not an AD login: it has no Kerberos protocol, no SPN and no
// domain controller. What it has instead is a network device (the NAS), a calling
// station and a service type. So RADIUS gets its own model rather than reusing
// adPolicyModel — the identity half is the same, the context half is entirely
// different.
//
// The JSON produced here is the `radius` block the backend seven-rule matcher reads
// (internal/radius/matcher.go, dto.Radius). Keep this file in step with that struct:
// a field the matcher does not read is dead, and a field it reads that the form
// omits silently widens the policy.

// ─── Actions ────────────────────────────────────────────────────────────────
// The engine maps policyFlow to allow / block / mfa_required, exactly as the AD
// and database engines do.
// Order and wording are the form's three action cards (handoff 2026-10-22): Require MFA
// first, as it is the default. `tone` is a tableUi tone key (StatusPill / SquareBadge).
export const RADIUS_ACTIONS = [
  { value: "mfa_required", label: "Require MFA", tone: "warn", hint: "Push to the user's phone" },
  { value: "allow", label: "Allow", tone: "success", hint: "Let in without MFA" },
  { value: "block", label: "Block", tone: "danger", hint: "Refuse the login" },
];

const actionMeta = (flow) => {
  const v = normalizeRadiusAction(flow);
  return RADIUS_ACTIONS.find((a) => a.value === v) || RADIUS_ACTIONS.find((a) => a.value === "allow");
};

export const RADIUS_ACTION_VALUES = RADIUS_ACTIONS.map((a) => a.value);
export const DEFAULT_RADIUS_ACTION = "mfa_required";

// Maps anything ever written into policyFlow onto the three values the engine
// accepts. Unknown → allow, which is what the evaluator does with a flow it does
// not recognise.
export function normalizeRadiusAction(flow) {
  const v = String(flow || "").toLowerCase();
  if (v === "mfa_required" || v === "mfa") return "mfa_required";
  if (v === "block" || v === "deny") return "block";
  return "allow";
}

// mfa_required → "Require MFA", allow → "Allow", block → "Block".
export function radiusActionLabel(flow) {
  return actionMeta(flow).label;
}

export function radiusActionTone(flow) {
  return actionMeta(flow).tone;
}

// ─── Service types ────────────────────────────────────────────────────────────
// Service-Type is a RADIUS integer attribute (RFC 2865). The backend canonicalises
// every spelling (6, "Administrative-User", "administrative") to one token through
// an alias table — these are those canonical tokens. There is NO backend lookup for
// them; this list is the source of truth the form offers.
//
// Empty selection = any service type. A login that arrives with no Service-Type is
// still enforced (the matcher over-enforces rather than silently exempting), so the
// form must not imply "leave blank to skip these logins".
export const RADIUS_SERVICE_TYPES = [
  { value: "login", label: "Login" },
  // { value: "framed", label: "Framed (VPN / PPP)" },
  // Relabelled: "(VPN / PPP)" pointed admins at the wrong choice for OpenVPN, which sends
  // Outbound (5). A Deny scoped to "framed" silently never applied to it in live testing.
  // IPsec VPNs (strongSwan, FortiGate, ASA) do send Framed. See SERVICE_TYPE_HINT.
  { value: "framed", label: "Framed" },
  { value: "callback-login", label: "Callback Login" },
  { value: "callback-framed", label: "Callback Framed" },
  { value: "outbound", label: "Outbound" },
  { value: "administrative", label: "Administrative" },
  { value: "nas-prompt", label: "NAS Prompt" },
  { value: "authenticate-only", label: "Authenticate Only" },
  { value: "callback-nas-prompt", label: "Callback NAS Prompt" },
  { value: "call-check", label: "Call Check" },
  { value: "callback-administrative", label: "Callback Administrative" },
];

export const RADIUS_SERVICE_TYPE_VALUES = RADIUS_SERVICE_TYPES.map((s) => s.value);

// What the policy form offers (handoff: Any, Framed, Outbound, Login, Administrative; "Any" is
// an empty selection). A saved policy carrying one of the other RFC values keeps it: the form
// adds it back as an option, so editing never drops it silently.
export const RADIUS_FORM_SERVICE_TYPES = ["framed", "outbound", "login", "administrative"].map((v) =>
  RADIUS_SERVICE_TYPES.find((s) => s.value === v),
);

export function serviceTypeLabel(value) {
  return (RADIUS_SERVICE_TYPES.find((s) => s.value === value) || {}).label || value;
}

// What a device actually sends varies by vendor, and guessing is how a policy ends up
// never firing. The bridge log prints the value that arrived on every login.
export const SERVICE_TYPE_HINT =
  "Check service= in the bridge log (/var/log/authnull/radius-bridge.log) to see what your device sends. OpenVPN sends Outbound; IPsec VPNs send Framed.";

// ─── Predicates ───────────────────────────────────────────────────────────────

const IPV4 =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

// A device (client) entry is a NAS-Identifier, an exact IP, a CIDR range, or "*".
// The wildcard and a plain identifier are both valid — the matcher tries the
// identifier first, then the address.
export function isValidClient(token) {
  const t = String(token || "").trim();
  if (!t) return false;
  if (t === "*") return true;
  if (isValidAddressPattern(t)) return true;
  // Otherwise treat it as a NAS-Identifier: a device name the admin recognises.
  return /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(t);
}

export const CLIENT_HINT =
  "A device name (vpn-hq), an exact IP, a CIDR range (10.10.0.0/24), or * for any device. Leave empty to cover ALL devices.";

// A calling-station entry is an exact value (MAC or IP), a CIDR, or "*".
export function isValidCallingStation(token) {
  const t = String(token || "").trim();
  if (!t) return false;
  if (t === "*") return true;
  if (isValidAddressPattern(t)) return true;
  // MAC or vendor-specific station id — accept the shape, let the engine compare.
  return /^[A-Za-z0-9][A-Za-z0-9:.\-_]*$/.test(t);
}

export const CALLING_STATION_HINT =
  "An IP, CIDR, MAC or * . Calling-Station-Id is reported by the client and can be omitted — use it to segment known endpoints, not as a security boundary.";

export const CALLING_STATION_NOTE = "Reported by the client. Useful for grouping, not a security boundary.";

// ─── Form validation ──────────────────────────────────────────────────────────
// The three messages shown above the Create button, in order. `existingNames` is the
// loaded policy list's names (the one being edited left out); names compare trimmed and
// case-insensitively.
export function radiusPolicyFormErrors({ policyName, existingNames = [], matchAll, groups, users, ous }) {
  const errors = [];
  const name = String(policyName || "").trim();
  if (!name) errors.push("Enter a policy name.");
  else if (existingNames.some((n) => String(n || "").trim().toLowerCase() === name.toLowerCase()))
    errors.push(`A policy called ${name} already exists.`);
  if (!matchAll && !(cleanTokens(groups).length || cleanTokens(users).length || cleanTokens(ous).length))
    errors.push("Choose who this applies to: everyone, or at least one group, user or OU.");
  return errors;
}

// An exact IPv4/IPv6 address or a CIDR of either family.
function isValidAddressPattern(token) {
  const t = String(token || "").trim();
  const [addr, mask, ...rest] = t.split("/");
  if (rest.length > 0) return false;
  if (addr.includes(":")) {
    if (!/^[0-9a-fA-F:]+$/.test(addr)) return false;
    if (mask === undefined) return true;
    return /^\d{1,3}$/.test(mask) && Number(mask) >= 0 && Number(mask) <= 128;
  }
  if (!IPV4.test(addr)) return false;
  if (mask === undefined) return true;
  return /^\d{1,2}$/.test(mask) && Number(mask) >= 0 && Number(mask) <= 32;
}

// Trims, drops blanks and de-duplicates — a stray "" would otherwise reach the
// matcher's cleanPatterns and be dropped there, but we keep the wire clean.
export function cleanTokens(list) {
  const seen = new Set();
  return (Array.isArray(list) ? list : [])
    .map((t) => String(t || "").trim())
    .filter((t) => t && !seen.has(t) && seen.add(t));
}

export function invalidTokens(list, validator) {
  return cleanTokens(list).filter((t) => !validator(t));
}

// ─── Payload builder ──────────────────────────────────────────────────────────
// Produces policyJson.radius exactly as dto.Radius expects it. Note the field name
// is `groups` even though the Go field is `Group` (singular); `domain` is a single
// string, not an array.
export function buildRadiusPolicyJson({
  policyName,
  policyFlow,
  domain,
  users,
  groups,
  ous,
  matchAll,
  clients,
  callingStations,
  serviceTypes,
}) {
  return {
    policyName,
    policyType: "radius",
    policyFlow: normalizeRadiusAction(policyFlow),
    radius: {
      domain: String(domain || "").trim(),
      users: cleanTokens(users),
      groups: cleanTokens(groups),
      ous: cleanTokens(ous),
      matchAll: !!matchAll,
      clients: cleanTokens(clients),
      callingStations: cleanTokens(callingStations),
      serviceTypes: cleanTokens(serviceTypes),
    },
  };
}

// Reads identity off a stored radius policy for the list view.
export function radiusIdentitySummary(policyJson) {
  const r = policyJson?.radius || {};
  if (r.matchAll) return "Everyone in the directory";
  const parts = [];
  if (r.users?.length) parts.push(`${r.users.length} user${r.users.length > 1 ? "s" : ""}`);
  if (r.groups?.length) parts.push(`${r.groups.length} group${r.groups.length > 1 ? "s" : ""}`);
  if (r.ous?.length) parts.push(`${r.ous.length} OU${r.ous.length > 1 ? "s" : ""}`);
  return parts.length ? parts.join(" · ") : "—";
}

export function radiusDeviceSummary(policyJson) {
  const clients = policyJson?.radius?.clients || [];
  if (!clients.length) return "All devices";
  if (clients.length === 1) return clients[0];
  return `${clients[0]} +${clients.length - 1}`;
}
